import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Plus, Trash2, Save, Loader2, Clock, Copy, ChevronDown, ChevronUp, CalendarDays,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const genId = () => Math.random().toString(36).slice(2, 10);

const StaffingRulesAdmin = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemRoles, setSystemRoles] = useState([]);
  const [expandedDays, setExpandedDays] = useState(new Set(['monday']));
  const [newRoleName, setNewRoleName] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesRes, rolesRes] = await Promise.all([
        axios.get(`${API}/staffing-rules`, { headers }),
        axios.get(`${API}/role-permissions`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRules(rulesRes.data);

      // Extract role names from role_permissions
      const rpRoles = (rolesRes.data || []).map(r => r.role).filter(Boolean);
      // Also grab any roles already used in blocks
  const blockRoles = (rulesRes.data.blocks || []).flatMap(b => [
        ...Object.keys(b.requirements || {}),
        ...Object.keys(b.on_call_requirements || {}),
      ]);
      const allRoles = [...new Set([...rpRoles, ...blockRoles])].sort();
      setSystemRoles(allRoles);
    } catch (err) {
      toast.error('Failed to load staffing rules');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/staffing-rules`, rules, { headers });
      setRules(res.data);
      toast.success('Staffing rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  const addBlock = (day) => {
    const newBlock = {
      id: genId(),
      day_of_week: day,
      start_time: '08:00',
      end_time: '16:00',
      label: '',
      requirements: {},
      on_call_requirements: {},
    };
    setRules(prev => ({ ...prev, blocks: [...(prev.blocks || []), newBlock] }));
    setExpandedDays(prev => new Set([...prev, day]));
  };

  const removeBlock = (id) => {
    setRules(prev => ({ ...prev, blocks: (prev.blocks || []).filter(b => b.id !== id) }));
  };

  const updateBlock = (id, field, value) => {
    setRules(prev => ({
      ...prev,
      blocks: (prev.blocks || []).map(b => b.id === id ? { ...b, [field]: value } : b),
    }));
  };

  const setRequirement = (blockId, role, count) => {
    setRules(prev => ({
      ...prev,
      blocks: (prev.blocks || []).map(b => {
        if (b.id !== blockId) return b;
        const req = { ...b.requirements };
        if (!count || count <= 0) delete req[role];
        else req[role] = parseInt(count);
        return { ...b, requirements: req };
      }),
    }));
  };

  const setOnCallRequirement = (blockId, role, count) => {
    setRules(prev => ({
      ...prev,
      blocks: (prev.blocks || []).map(b => {
        if (b.id !== blockId) return b;
        const req = { ...(b.on_call_requirements || {}) };
        if (!count || count <= 0) delete req[role];
        else req[role] = parseInt(count);
        return { ...b, on_call_requirements: req };
      }),
    }));
  };

  const addRoleToBlock = (blockId, role) => {
    if (!role) return;
    setRequirement(blockId, role, 1);
  };

  const addOnCallRoleToBlock = (blockId, role) => {
    if (!role) return;
    setOnCallRequirement(blockId, role, 1);
  };

  const addCustomRole = () => {
    const r = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!r) return;
    if (!systemRoles.includes(r)) {
      setSystemRoles(prev => [...prev, r].sort());
    }
    setNewRoleName('');
    toast.success(`Role "${r}" added`);
  };

  const copyDayBlocks = (fromDay, toDay) => {
    const src = (rules.blocks || []).filter(b => b.day_of_week === fromDay);
    if (!src.length) { toast.error(`No blocks to copy from ${DAY_SHORT[fromDay]}`); return; }
    // Remove existing blocks for target day, add copies
    const other = (rules.blocks || []).filter(b => b.day_of_week !== toDay);
    const copies = src.map(b => ({
      ...b,
      id: genId(),
      day_of_week: toDay,
      requirements: { ...b.requirements },
      on_call_requirements: { ...(b.on_call_requirements || {}) },
    }));
    setRules(prev => ({ ...prev, blocks: [...other, ...copies] }));
    setExpandedDays(prev => new Set([...prev, toDay]));
    toast.success(`Copied ${src.length} block(s) to ${DAY_LABELS[toDay]}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading staffing rules...
      </div>
    );
  }

  if (!rules) return null;

  const blocksByDay = {};
  DAYS.forEach(d => { blocksByDay[d] = (rules.blocks || []).filter(b => b.day_of_week === d).sort((a, b) => a.start_time.localeCompare(b.start_time)); });

  // Find days that have blocks (for copy-from feature)
  const daysWithBlocks = DAYS.filter(d => blocksByDay[d].length > 0);

  return (
    <div className="space-y-6" data-testid="staffing-rules-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Staffing Rules</h3>
          <p className="text-sm text-slate-500">
            Define operating days, staffing requirements per time block, and on-call needs. Used by AI, coverage comparison, and the Today dashboard.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-500"
          data-testid="save-staffing-rules"
        >
          {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Saving...</> : <><Save className="h-4 w-4 mr-1.5" />Save Rules</>}
        </Button>
      </div>

      {/* Operating Days & Business Hours */}
      <Card className="border-slate-200">
        <CardContent className="py-4 px-4 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-xs font-semibold text-slate-600">Operating Days</span>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="operating-days">
              {DAYS.map(day => {
                const active = (rules.operating_days || []).includes(day);
                return (
                  <button
                    key={day}
                    onClick={() => {
                      const days = rules.operating_days || [];
                      setRules(prev => ({
                        ...prev,
                        operating_days: active ? days.filter(d => d !== day) : [...days, day],
                      }));
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      active
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid={`opday-${day}`}
                  >
                    {DAY_LABELS[day].slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Business Hours Start</label>
              <Input
                type="time"
                value={rules.business_hours_start || '08:00'}
                onChange={e => setRules(prev => ({ ...prev, business_hours_start: e.target.value }))}
                className="h-8 text-sm"
                data-testid="business-hours-start"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Business Hours End</label>
              <Input
                type="time"
                value={rules.business_hours_end || '20:00'}
                onChange={e => setRules(prev => ({ ...prev, business_hours_end: e.target.value }))}
                className="h-8 text-sm"
                data-testid="business-hours-end"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Notes for AI</label>
            <textarea
              value={rules.notes || ''}
              onChange={e => setRules(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="E.g., We always need at least 1 team lead on Mondays..."
              className="w-full text-sm border rounded-md p-2 h-16 resize-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 border-slate-200"
              data-testid="staffing-notes"
            />
          </div>
        </CardContent>
      </Card>

      {/* Available Roles */}
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-600">Available Roles:</span>
            {systemRoles.length === 0 && (
              <span className="text-xs text-slate-400 italic">No roles found. Add roles below to get started.</span>
            )}
            {systemRoles.map(r => (
              <Badge key={r} variant="secondary" className="text-xs capitalize">{r.replace(/_/g, ' ')}</Badge>
            ))}
            <div className="flex gap-1 ml-2">
              <Input
                placeholder="New role name..."
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomRole(); }}}
                className="h-7 text-xs w-36"
                data-testid="add-role-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={addCustomRole}
                disabled={!newRoleName.trim()}
                className="h-7 px-2"
                data-testid="add-role-btn"
              >
                <Plus className="h-3 w-3 mr-0.5" />Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day-by-day */}
      <div className="space-y-2">
        {DAYS.map(day => {
          const isExpanded = expandedDays.has(day);
          const dayBlocks = blocksByDay[day];
          const totalReqs = dayBlocks.reduce((sum, b) => sum + Object.values(b.requirements || {}).reduce((s, v) => s + v, 0), 0);
          const totalOnCallReqs = dayBlocks.reduce((sum, b) => sum + Object.values(b.on_call_requirements || {}).reduce((s, v) => s + v, 0), 0);

          return (
            <Card key={day} className="border-slate-200 overflow-hidden" data-testid={`day-${day}`}>
              {/* Day header */}
              <div
                onClick={() => toggleDay(day)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left cursor-pointer"
                data-testid={`toggle-day-${day}`}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleDay(day); }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-800">{DAY_LABELS[day]}</span>
                  {dayBlocks.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        {dayBlocks.length} block{dayBlocks.length !== 1 ? 's' : ''}
                      </Badge>
                      {totalReqs > 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {totalReqs} staff required
                        </Badge>
                      )}
                      {totalOnCallReqs > 0 && (
                        <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">
                          {totalOnCallReqs} on-call
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">No blocks configured</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); addBlock(day); }}
                    className="h-6 text-[10px] px-2"
                    data-testid={`add-block-${day}`}
                  >
                    <Plus className="h-3 w-3 mr-0.5" />Add Block
                  </Button>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <CardContent className="p-3 space-y-3 border-t border-slate-100">
                  {dayBlocks.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-xs text-slate-400 mb-2">No time blocks defined for {DAY_LABELS[day]}</p>
                      {daysWithBlocks.length > 0 && (
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-[10px] text-slate-400">Copy from:</span>
                          {daysWithBlocks.filter(d => d !== day).map(d => (
                            <Button
                              key={d}
                              size="sm"
                              variant="outline"
                              onClick={() => copyDayBlocks(d, day)}
                              className="h-6 text-[10px] px-2"
                              data-testid={`copy-${d}-to-${day}`}
                            >
                              <Copy className="h-2.5 w-2.5 mr-0.5" />{DAY_SHORT[d]}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {dayBlocks.map(block => (
                    <div
                      key={block.id}
                      className="border border-slate-200 rounded-lg overflow-hidden"
                      data-testid={`block-${block.id}`}
                    >
                      {/* Block header: times + label */}
                      <div className="flex items-center gap-3 px-3 py-2 bg-white border-b border-slate-100">
                        <Clock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <Input
                          type="time"
                          value={block.start_time}
                          onChange={e => updateBlock(block.id, 'start_time', e.target.value)}
                          className="h-7 text-xs w-28"
                          data-testid={`block-start-${block.id}`}
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <Input
                          type="time"
                          value={block.end_time}
                          onChange={e => updateBlock(block.id, 'end_time', e.target.value)}
                          className="h-7 text-xs w-28"
                          data-testid={`block-end-${block.id}`}
                        />
                        <Input
                          placeholder="Label (e.g. Day Shift)"
                          value={block.label || ''}
                          onChange={e => updateBlock(block.id, 'label', e.target.value)}
                          className="h-7 text-xs flex-1"
                          data-testid={`block-label-${block.id}`}
                        />
                        <button
                          onClick={() => removeBlock(block.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-block-${block.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Role requirements */}
                      <div className="px-3 py-2 bg-slate-50/50">
                        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                          Regular Shift — Minimum Staff Required
                        </div>

                        {Object.keys(block.requirements || {}).length === 0 && systemRoles.length === 0 && (
                          <p className="text-xs text-slate-400 italic py-1">
                            Add roles above first, then set requirements here
                          </p>
                        )}

                        {/* Show existing requirements */}
                        <div className="flex flex-wrap gap-2 items-center">
                          {Object.entries(block.requirements || {}).map(([role, count]) => (
                            <div key={role} className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1">
                              <span className="text-xs font-medium text-slate-700 capitalize">{role.replace(/_/g, ' ')}</span>
                              <Input
                                type="number"
                                min={0}
                                value={count}
                                onChange={e => setRequirement(block.id, role, e.target.value)}
                                className="h-6 text-xs w-14 text-center border-0 bg-transparent p-0"
                                data-testid={`req-${block.id}-${role}`}
                              />
                              <button
                                onClick={() => setRequirement(block.id, role, 0)}
                                className="text-slate-300 hover:text-red-500"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}

                          {/* Add requirement dropdown */}
                          {systemRoles.filter(r => !(block.requirements || {})[r]).length > 0 && (
                            <select
                              onChange={e => { if (e.target.value) { addRoleToBlock(block.id, e.target.value); e.target.value = ''; } }}
                              className="h-7 text-xs border border-dashed border-slate-300 rounded-md px-2 bg-white text-slate-500 cursor-pointer hover:border-indigo-400 hover:text-indigo-600"
                              defaultValue=""
                              data-testid={`add-req-${block.id}`}
                            >
                              <option value="" disabled>+ Add role...</option>
                              {systemRoles.filter(r => !(block.requirements || {})[r]).map(r => (
                                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>

                      {/* On-Call requirements */}
                      <div className="px-3 py-2 bg-orange-50/40 border-t border-orange-100">
                        <div className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide mb-1.5">
                          On-Call — Minimum Staff Required
                        </div>

                        <div className="flex flex-wrap gap-2 items-center">
                          {Object.entries(block.on_call_requirements || {}).map(([role, count]) => (
                            <div key={role} className="flex items-center gap-1 bg-white border border-orange-200 rounded-md px-2 py-1">
                              <span className="text-xs font-medium text-orange-700 capitalize">{role.replace(/_/g, ' ')}</span>
                              <Input
                                type="number"
                                min={0}
                                value={count}
                                onChange={e => setOnCallRequirement(block.id, role, e.target.value)}
                                className="h-6 text-xs w-14 text-center border-0 bg-transparent p-0"
                                data-testid={`oncall-req-${block.id}-${role}`}
                              />
                              <button
                                onClick={() => setOnCallRequirement(block.id, role, 0)}
                                className="text-orange-300 hover:text-red-500"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}

                          {/* Add on-call role dropdown */}
                          {systemRoles.filter(r => !(block.on_call_requirements || {})[r]).length > 0 && (
                            <select
                              onChange={e => { if (e.target.value) { addOnCallRoleToBlock(block.id, e.target.value); e.target.value = ''; } }}
                              className="h-7 text-xs border border-dashed border-orange-300 rounded-md px-2 bg-white text-orange-500 cursor-pointer hover:border-orange-500 hover:text-orange-700"
                              defaultValue=""
                              data-testid={`add-oncall-req-${block.id}`}
                            >
                              <option value="" disabled>+ Add on-call role...</option>
                              {systemRoles.filter(r => !(block.on_call_requirements || {})[r]).map(r => (
                                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          )}

                          {Object.keys(block.on_call_requirements || {}).length === 0 && (
                            <span className="text-[10px] text-orange-400 italic">No on-call requirements set</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Copy to other days */}
                  {dayBlocks.length > 0 && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-[10px] text-slate-400">Copy this day's blocks to:</span>
                      {DAYS.filter(d => d !== day).map(d => (
                        <button
                          key={d}
                          onClick={() => copyDayBlocks(day, d)}
                          className="text-[10px] px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
                          data-testid={`copy-to-${d}`}
                        >
                          {DAY_SHORT[d]}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StaffingRulesAdmin;
